/**
 * L'horloge de la saison, dérivée des SEULES dates servies par l'API
 * (`startedAt` et `endsAt` de la saison, déjà dans la charge de `/api/me`).
 *
 * ⚠️ NE PAS dériver le compteur de `SEASON_LENGTH_WEEKS` (front/src/config.ts).
 * Cette constante ne décrit que la durée d'une saison NEUVE ; une saison vivante
 * porte ses propres dates. La Saison du Calibrage, ouverte quand la durée valait
 * seize semaines, en est la preuve permanente : une constante lui a déjà fait
 * afficher « semaine 8 sur 8 » pendant les cinquante-trois jours qui lui
 * restaient. Le total vient de la ligne, toujours.
 *
 * Miroir volontaire de la formule serveur, `App\Domain\Season\SeasonCalendar` :
 * plafond au septième de jour sur la durée totale, plancher sur l'écoulé,
 * plafond sur les jours restants. Le doublon est assumé, le montage
 * cross-langage étant écarté (les deux conteneurs de dev sont cloisonnés, donc
 * un test qui lit les deux arbres ne passerait qu'en CI). Chaque côté est
 * épinglé par un test de son propre littéral, et le contrat est à sens unique :
 * si la formule PHP change, c'est ce fichier qu'on réaligne, jamais l'inverse.
 * La table de référence vit dans `seasonClock.test.ts`.
 *
 * Zéro requête de plus : la saison active arrive avec le capitaine.
 */
export type SeasonUrgency = 'calm' | 'soon' | 'imminent'

/**
 * Le sablier s'allume à quatorze jours du terme : c'est le préavis de reset
 * exigé par le runbook de la fiche 31, et le seul que le jeu donne. Avant ce
 * seuil, le compte à rebours n'existe pas dans la barre.
 */
export const COUNTDOWN_VISIBLE_FROM_DAYS = 14

/**
 * En deçà, le sablier vire à l'ambre. Sept jours, et non trois : c'est le seuil
 * que l'en-tête du Grand Chantier emploie déjà (`Chantier.tsx`), et deux seuils
 * sur la même échéance suffisent.
 */
export const IMMINENT_DAYS = 7

const DAY_MS = 86_400_000

export interface SeasonClock {
  /** Jours restants avant le terme, jour entamé compté, jamais négatif. */
  daysRemaining: number
  /** Semaine en cours, comptée à partir de 1 et bornée à la dernière. */
  week: number
  totalWeeks: number
  /** Avancement de la saison, 0 au premier jour, 1 à l'échéance. */
  progress: number
  urgency: SeasonUrgency
}

/**
 * Rend null sur toute ligne de saison inexploitable (dates absentes, illisibles,
 * ou échéance qui ne suit pas l'ouverture) plutôt qu'un NaN à l'écran. Les
 * appelants n'affichent rien dans ce cas.
 */
export function seasonClock(
  startedAt: string,
  endsAt: string,
  now: number = Date.now(),
): SeasonClock | null {
  const start = Date.parse(startedAt)
  const end = Date.parse(endsAt)
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null

  const totalDays = Math.max(1, Math.ceil((end - start) / DAY_MS))
  const totalWeeks = Math.max(1, Math.ceil(totalDays / 7))
  const elapsedDays = Math.max(0, Math.floor((now - start) / DAY_MS))
  const daysRemaining = Math.max(0, Math.ceil((end - now) / DAY_MS))

  return {
    daysRemaining,
    week: Math.max(1, Math.min(totalWeeks, Math.floor(elapsedDays / 7) + 1)),
    totalWeeks,
    progress: Math.max(0, Math.min(1, (now - start) / (end - start))),
    urgency:
      daysRemaining <= IMMINENT_DAYS
        ? 'imminent'
        : daysRemaining <= COUNTDOWN_VISIBLE_FROM_DAYS
          ? 'soon'
          : 'calm',
  }
}

/** Le libellé lu par le capitaine : « 53 jours », « 1 jour », « dernier jour ». */
export function remainingLabel(daysRemaining: number): string {
  if (daysRemaining <= 0) return 'dernier jour'

  return `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''}`
}
